import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import {
  FeedAlgorithm,
  FeedAlgorithmRunHistoryEntry,
  FeedAlgorithmStatus,
} from 'src/app/core/models/algorithm.models';

interface DisplayParam {
  label: string;
  value: string;
}

@Component({
  selector: 'app-algorithm-list-card',
  templateUrl: './algorithm-list-card.component.html',
  styleUrls: ['./algorithm-list-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AlgorithmListCardComponent {
  @Input() algorithm!: FeedAlgorithm;
  @Input() pending: boolean = false;

  @Input() editable: boolean = false;

  @Output() detach = new EventEmitter<FeedAlgorithm>();
  @Output() edit = new EventEmitter<FeedAlgorithm>();

  get status(): FeedAlgorithmStatus {
    return this.algorithm?.status ?? 'ok';
  }

  get runsCount(): number {
    return this.algorithm?.runs_count ?? 0;
  }

  get lastRunRelative(): string | null {
    return this.algorithm?.last_run_relative ?? null;
  }

  get runHistory(): FeedAlgorithmRunHistoryEntry[] {
    return this.algorithm?.run_history ?? [];
  }

  get hasRuntimeInfo(): boolean {
    return (
      this.lastRunRelative !== null ||
      this.runsCount > 0 ||
      this.runHistory.length > 0
    );
  }

  get displayParams(): DisplayParam[] {
    const params = this.algorithm?.params;
    if (!params) return [];

    if (Array.isArray(params)) {
      return params
        .map((p) => ({
          label: this.paramLabel(p),
          value: this.paramValue(p),
        }))
        .filter((p) => p.label || p.value);
    }

    if (typeof params === 'object') {
      return Object.entries(params)
        .map(([key, value]) => ({
          label: key,
          value: this.paramValue({ value }),
        }))
        .filter((p) => p.label || p.value);
    }

    return [];
  }

  private paramLabel(param: any): string {
    return param?.label ?? param?.name ?? param?.key ?? param?.k ?? '';
  }

  private paramValue(param: any): string {
    const value = param?.value ?? param?.v;
    if (value === undefined || value === null) return '';
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
    if (Array.isArray(value)) return value.join(', ');
    return JSON.stringify(value);
  }

  onDetach(): void {
    if (this.pending) return;
    this.detach.emit(this.algorithm);
  }

  onEdit(): void {
    if (this.pending) return;
    this.edit.emit(this.algorithm);
  }
}
